import React, { useMemo } from 'react';
import { AdminPanelAlert, AdminPanelHero, AdminPanelSection, AdminPanelTableWrap } from './AdminPanelUi';
import { exportProductsCsv, printProductsList } from './adminExportUtils';
import { productOnSale, productSalePrice } from '../productPricing';
import { hrefForSameSiteAsset, pathLooksLikeVideo, productGalleryImagePaths } from '../productMediaUtils';
import { productStockBucket } from './adminCheckoutUtils';
import { catalogSubcategoryHealth, subcategoryNeedsWork } from './catalogHealthUtils';

function stockLabel(p) {
  const sq = p?.stock_qty;
  if (sq === null || sq === undefined) return 'Unlimited';
  const n = Number(sq);
  if (!Number.isFinite(n) || n <= 0) return 'Out';
  return String(Math.floor(n));
}

function adminProductThumbSrc(p) {
  for (const u of productGalleryImagePaths(p)) {
    if (!pathLooksLikeVideo(u)) return hrefForSameSiteAsset(u);
  }
  return '';
}

export default function AdminProductsPanel({
  sortedEditableCategories,
  selectedCategory,
  setSelectedCategory,
  editableProducts,
  productSearch,
  setProductSearch,
  stockFilter,
  setStockFilter,
  onAddProduct,
  onEditProduct,
  onDeleteProduct,
}) {
  const filtered = useMemo(() => {
    const list = editableProducts[selectedCategory] || [];
    const q = productSearch.trim().toLowerCase();
    return list
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => {
        if (stockFilter) {
          const bucket = productStockBucket(p);
          if (stockFilter === 'out' && bucket !== 'out') return false;
          if (stockFilter === 'low' && bucket !== 'low') return false;
          if (stockFilter === 'unlimited' && bucket !== 'unlimited') return false;
          if (stockFilter === 'tracked' && bucket === 'unlimited') return false;
          if (stockFilter === 'missing-sub' && !subcategoryNeedsWork(p.subcategory)) return false;
          if (stockFilter === 'has-sub' && subcategoryNeedsWork(p.subcategory)) return false;
        }
        if (!q) return true;
        const hay = `${p.name} ${p.id} ${p.type} ${p.subcategory}`.toLowerCase();
        return hay.includes(q);
      });
  }, [editableProducts, selectedCategory, productSearch, stockFilter]);

  const subHealth = useMemo(
    () => catalogSubcategoryHealth(editableProducts, sortedEditableCategories),
    [editableProducts, sortedEditableCategories]
  );

  const selectedCatHealth = subHealth.byCategory.find((c) => c.slug === selectedCategory);

  const selectedCatName =
    sortedEditableCategories.find((c) => c.slug === selectedCategory)?.name || selectedCategory;

  const stockFilterLabels = {
    out: 'Out of stock',
    low: 'Low stock',
    tracked: 'Tracked only',
    unlimited: 'Unlimited',
    'missing-sub': 'Subcategory missing',
    'has-sub': 'Has subcategory',
  };

  return (
    <div className="admin-products-panel admin-panel-workspace-root">
      <AdminPanelHero
        title="Products"
        lead="Edit catalog items by category. Changes publish automatically."
        actions={
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => exportProductsCsv(sortedEditableCategories, editableProducts)}
              title="All categories — opens in Excel"
            >
              Export Excel (CSV)
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() =>
                exportProductsCsv(sortedEditableCategories, editableProducts, {
                  categorySlug: selectedCategory,
                })
              }
              title="Current category only"
            >
              Export category
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() =>
                printProductsList({
                  categoryName: selectedCatName,
                  products: filtered,
                  searchLabel: productSearch.trim(),
                  stockFilterLabel: stockFilterLabels[stockFilter] || '',
                })
              }
              title="Print with product photos — Save as PDF in print dialog (may be a large file)"
            >
              Print catalog (with images)
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => onAddProduct(selectedCategory)}
            >
              + Add product
            </button>
          </div>
        }
      />

      <AdminPanelSection title={`${selectedCatName} catalog`} subtitle="Filter, search, and export the list below.">
        {!subHealth.ok && subHealth.total > 0 ? (
          <AdminPanelAlert variant="warning">
            <strong>{subHealth.needsWork}</strong> of {subHealth.total} products need a subcategory (not blank or
            &ldquo;all&rdquo;). Gift finder and shop groups depend on this.
          </AdminPanelAlert>
        ) : subHealth.total > 0 ? (
          <AdminPanelAlert variant="success">
            All {subHealth.total} products have subcategories set.
          </AdminPanelAlert>
        ) : null}

        {selectedCatHealth && selectedCatHealth.needsWork > 0 ? (
          <p className="small text-muted mb-3">
            In <strong>{selectedCatHealth.name}</strong>: {selectedCatHealth.needsWork} of {selectedCatHealth.total}{' '}
            need subcategory.
          </p>
        ) : null}

        <div className="admin-panel-toolbar mb-3 d-flex flex-wrap gap-2 align-items-center">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => onAddProduct(selectedCategory)}
          >
            + Add product
          </button>
          <label className="small text-muted mb-0" htmlFor="admin-product-category">
            Category
          </label>
          <select
            id="admin-product-category"
            className="form-select form-select-sm"
            style={{ maxWidth: 220 }}
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {sortedEditableCategories.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name} ({editableProducts[c.slug]?.length || 0})
              </option>
            ))}
          </select>
          <label htmlFor="admin-product-search" className="visually-hidden">
            Search products
          </label>
          <input
            id="admin-product-search"
            type="search"
            className="form-control form-control-sm flex-grow-1"
            style={{ minWidth: 160, maxWidth: 280 }}
            placeholder="Search name, ID, type…"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
          />
          <label className="visually-hidden" htmlFor="admin-product-stock-filter">
            Stock filter
          </label>
          <select
            id="admin-product-stock-filter"
            className="form-select form-select-sm"
            style={{ maxWidth: 140 }}
            value={stockFilter}
            onChange={(e) => setStockFilter(e.target.value)}
          >
            <option value="">All stock</option>
            <option value="out">Out of stock</option>
            <option value="low">Low (≤3)</option>
            <option value="tracked">Tracked only</option>
            <option value="unlimited">Unlimited</option>
            <option value="missing-sub">Subcategory missing</option>
            <option value="has-sub">Has subcategory</option>
          </select>
        </div>

        <AdminPanelTableWrap>
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th style={{ width: 56 }}> </th>
                <th>Name</th>
                <th>Shop</th>
                <th>Stock</th>
                <th>ID</th>
                <th>Type</th>
                <th>Sub</th>
                <th className="text-end">Price</th>
                <th style={{ width: 140 }}> </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">
                    No products in this category. Click <strong>Add product</strong>.
                  </td>
                </tr>
              ) : (
                filtered.map(({ p, i }) => {
                  const thumb = adminProductThumbSrc(p);
                  return (
                  <tr key={`${p.id}-${i}`}>
                    <td>
                      {thumb ? (
                        <img src={thumb} alt="" className="rounded" style={{ width: 44, height: 44, objectFit: 'cover' }} />
                      ) : (
                        <div className="bg-light border rounded" style={{ width: 44, height: 44 }} />
                      )}
                    </td>
                    <td className="fw-medium">{p.name || '—'}</td>
                    <td className="small">
                      {p.is_visible === false ? (
                        <span className="badge bg-secondary">Hidden</span>
                      ) : (
                        <span className="text-success">Live</span>
                      )}
                    </td>
                    <td className="small">
                      {stockLabel(p) === 'Out' ? (
                        <span className="badge bg-danger">Out</span>
                      ) : stockLabel(p) === 'Unlimited' ? (
                        <span className="text-muted">∞</span>
                      ) : (
                        stockLabel(p)
                      )}
                    </td>
                    <td>
                      <code className="small">{p.id || '—'}</code>
                    </td>
                    <td className="small">{p.type || '—'}</td>
                    <td className="small">
                      {subcategoryNeedsWork(p.subcategory) ? (
                        <span className="badge bg-warning text-dark">Fix</span>
                      ) : (
                        p.subcategory
                      )}
                    </td>
                    <td className="text-end small">
                      {productOnSale(p) ? (
                        <>
                          <span className="text-muted text-decoration-line-through me-1">
                            ₹{Number(p.price).toFixed(0)}
                          </span>
                          <span className="text-success fw-semibold">₹{Number(productSalePrice(p)).toFixed(0)}</span>
                        </>
                      ) : (
                        <>₹{Number(p.price).toFixed(2)}</>
                      )}
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button type="button" className="btn btn-outline-primary" onClick={() => onEditProduct(selectedCategory, i)}>
                          Edit
                        </button>
                        <button type="button" className="btn btn-outline-danger" onClick={() => onDeleteProduct(selectedCategory, i)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </AdminPanelTableWrap>
      </AdminPanelSection>
    </div>
  );
}
